import { useInfiniteQuery, useQuery as useTanstackQuery } from '@tanstack/react-query';
import * as _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../contexts/RouterContext';
import { useAddToast } from '../../contexts/ToastContext';
import { useProjectsByLocators } from '../../hooks/api/projects/useProjectsByLocators';
import { getReleaseGroupTitleQueryKey, useReleaseGroupsByIds } from '../../hooks/api/releaseGroups/useReleaseGroupsByIds';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import queryClient from '../../ReactQuery/client';
import { get } from '../../redux/api';
import LabeledSelect from '../generic/Forms/LabeledSelect';
import { COUNT_MAX_FE } from '../../../shared/projectsV2/request';
import type { ProjectType } from '../../../shared/projectsV2/common';
import type { Project, ProjectsResponse, ReleaseGroupsResponse } from '../../../shared/projectsV2/response';

import styles from './LegalAgentScopeSelect.module.less';
import { LegalAgentScopeList } from './LegalAgentScopeList';
import { parseLegalAgentQuery, serializeLegalAgentQuery, withScopeKind } from './legalAgentQuery';
import type { LegalAgentQuery, LegalAgentScopeKind } from './legalAgentQuery';

export type ScopeOption = {
  label: string;
  value: string;
  //  Projects only, for the row's type and private icons. Null means release groups or not known yet.
  type: ProjectType | null;
  isPrivate: boolean | null;
};

const PAGE_SIZE = 20;

type OptionsPage = {
  options: ScopeOption[];
  hasMore: boolean;
  //  Every match for the search, across pages.
  total: number;
};

function toProjectOption(project: Project): ScopeOption {
  return { label: project.title, value: project.id, type: project.type, isPrivate: !project.public };
}

type LoadOptions = (search: string, page: number) => Promise<OptionsPage>;

export async function loadProjectOptions(search: string, page: number): Promise<OptionsPage> {
  const params = { count: PAGE_SIZE, page, sort: 'title_asc', title: search };
  const response = await get<ProjectsResponse>('/api/v2/projects', false, params, { encodeQuery: true });
  //  A response with no body (an HTML redirect, say) is a failure, not an empty list.
  if (!response.data) {
    throw new Error('The projects response had no body');
  }
  const { projects, total } = response.data;
  return { options: projects.map(toProjectOption), hasMore: page * PAGE_SIZE < total, total };
}

export async function loadReleaseGroupOptions(search: string, page: number): Promise<OptionsPage> {
  const params = { count: PAGE_SIZE, page, sort: 'title_asc', title: search };
  const response = await get<ReleaseGroupsResponse>('/api/v2/release-groups', false, params, { encodeQuery: true });
  if (!response.data) {
    throw new Error('The release groups response had no body');
  }
  const { releaseGroups, total } = response.data;
  return {
    options: releaseGroups.map((group): ScopeOption => ({
      label: group.title,
      value: String(group.id),
      type: null,
      isPrivate: null,
    })),
    hasMore: page * PAGE_SIZE < total,
    total,
  };
}

const LOADERS: Record<LegalAgentScopeKind, LoadOptions> = {
  projects: loadProjectOptions,
  releaseGroups: loadReleaseGroupOptions,
};

//  Callers pass at most LEGAL_AGENT_SCOPE_MAX matches, which one page answers for either kind.
export async function loadAllMatchingOptions(
  kind: LegalAgentScopeKind,
  search: string,
  matchCount: number,
): Promise<ScopeOption[]> {
  if (kind === 'projects') {
    const params = { count: matchCount, page: 1, sort: 'title_asc', title: search };
    const response = await get<ProjectsResponse>('/api/v2/projects', false, params, { encodeQuery: true });
    if (!response.data) {
      throw new Error('The projects response had no body');
    }
    return response.data.projects.map(toProjectOption);
  }
  const params = { count: COUNT_MAX_FE, page: 1, sort: 'title_asc', title: search };
  const response = await get<ReleaseGroupsResponse>('/api/v2/release-groups', false, params, { encodeQuery: true });
  if (!response.data) {
    throw new Error('The release groups response had no body');
  }
  return response.data.releaseGroups.map((group): ScopeOption => ({
    label: group.title,
    value: String(group.id),
    type: null,
    isPrivate: null,
  }));
}

type ScopePage = OptionsPage & { kind: LegalAgentScopeKind };

//  Every entity of the kind, for the "Work with" dropdown counts and the count shown while searching.
function useScopeKindTotal(kind: LegalAgentScopeKind): number | null {
  const { data } = useTanstackQuery({
    queryKey: ['legal-agent', 'scope-total', kind],
    queryFn: () => LOADERS[kind]('', 1),
    staleTime: 1000 * 60,
  });
  return data?.total ?? null;
}

type KindOption = { label: string; value: LegalAgentScopeKind };

const withTotal = (label: string, total: number | null) => (total === null ? label : `${label} (${total})`);

function kindOptions(projectTotal: number | null, releaseGroupTotal: number | null): KindOption[] {
  return [
    { label: withTotal('Projects', projectTotal), value: 'projects' },
    { label: withTotal('Release Groups', releaseGroupTotal), value: 'releaseGroups' },
  ];
}

/**
 * The Legal Agent portal's scope: the projects or release groups whose activity to show. Empty means every
 * viewable one of that kind.
 */
export const LegalAgentScopeSelect: VoidFunctionComponent = () => {
  const [query, setQuery] = useQuery<LegalAgentQuery, Record<string, unknown>>(parseLegalAgentQuery);
  const addToast = useAddToast();
  const isReleaseGroups = query.scope === 'releaseGroups';
  const releaseGroups = useReleaseGroupsByIds(isReleaseGroups ? query.releaseGroups : []);

  //  Projects checked in the list carry their titles and icons. The lookup only labels locators restored from the URL.
  const [pickedOptions, setPickedOptions] = useState<Map<string, ScopeOption>>(new Map());
  const unlabeled = useMemo(
    () => (isReleaseGroups ? [] : query.projects.filter((locator) => !pickedOptions.has(locator))),
    [isReleaseGroups, query.projects, pickedOptions],
  );
  const { data, isError } = useProjectsByLocators(unlabeled);
  const lookedUp = new Map((data?.projects ?? []).map((project) => [project.id, toProjectOption(project)]));

  //  The effect reruns when the query or callbacks change; each lookup result is acted on once.
  const handledLookup = useRef<unknown>(null);
  useEffect(() => {
    if (!data || handledLookup.current === data) {
      return;
    }
    handledLookup.current = data;
    const found = new Set(data.projects.map((project) => project.id));
    const missing = unlabeled.filter((locator) => !found.has(locator));
    if (missing.length === 0) {
      return;
    }
    setQuery(serializeLegalAgentQuery({
      ...query,
      projects: query.projects.filter((locator) => !missing.includes(locator)),
    }));
    addToast({
      type: 'error',
      content: missing.length === 1
        ? '1 selected project isn\'t available and was removed.'
        : `${missing.length} selected projects aren't available and were removed.`,
    });
  }, [data, unlabeled, query, setQuery, addToast]);

  const handledUnavailable = useRef('');
  const unavailableKey = releaseGroups.settled ? releaseGroups.unavailable.join(',') : '';
  useEffect(() => {
    if (unavailableKey === '') {
      handledUnavailable.current = '';
      return;
    }
    if (handledUnavailable.current === unavailableKey) {
      return;
    }
    handledUnavailable.current = unavailableKey;
    const missing = unavailableKey.split(',');
    setQuery(serializeLegalAgentQuery({
      ...query,
      releaseGroups: query.releaseGroups.filter((id) => !missing.includes(id)),
    }));
    addToast({
      type: 'error',
      content: missing.length === 1
        ? '1 selected release group isn\'t available and was removed.'
        : `${missing.length} selected release groups aren't available and were removed.`,
    });
  }, [unavailableKey, query, setQuery, addToast]);

  const lookupFailed = isReleaseGroups ? releaseGroups.hasError : isError;
  useEffect(() => {
    if (lookupFailed) {
      addToast({
        type: 'error',
        content: `Selected ${isReleaseGroups ? 'release groups' : 'projects'} couldn't be loaded. Try again in a moment.`,
      });
    }
  }, [lookupFailed, isReleaseGroups, addToast]);

  const selected = isReleaseGroups ? query.releaseGroups : query.projects;
  const selectedOptions: ScopeOption[] = isReleaseGroups
    ? query.releaseGroups.map((id) => ({ label: releaseGroups.titles.get(id) ?? id, value: id, type: null, isPrivate: null }))
    : query.projects.map((locator) => pickedOptions.get(locator) ?? lookedUp.get(locator) ?? { label: locator, value: locator, type: null, isPrivate: null });

  const [search, setSearch] = useState('');
  const { debouncedValue: laggedSearch } = useDebouncedValue(search, 200);
  //  Clearing the search applies at once. The "Work with" dropdown clears it, so a switch there never queries the new
  //  kind with the old text; other changes of scope (back and forward) keep the typed search.
  const debouncedSearch = search === '' ? '' : laggedSearch;
  const results = useInfiniteQuery({
    queryKey: ['legal-agent', 'scope-list', query.scope, debouncedSearch],
    queryFn: async ({ pageParam = 1 }): Promise<ScopePage> => ({
      ...(await LOADERS[query.scope](debouncedSearch, pageParam)),
      kind: query.scope,
    }),
    getNextPageParam: (page, pages) => (page.hasMore ? pages.length + 1 : undefined),
    //  A new search keeps showing the previous results until its first page arrives. React Query v4 has no
    //  replacement for this option; its placeholderData callback receives no previous data.
    // oxlint-disable-next-line typescript/no-deprecated
    keepPreviousData: true,
  });
  //  Kept results are only for a new search within the same kind; the other kind's rows must never be clickable.
  const kindPages = (results.data?.pages ?? []).filter((page) => page.kind === query.scope);
  const hasKindPages = kindPages.length > 0;
  const matchCount = kindPages[0]?.total ?? null;
  //  The dropdown needs both kinds' totals, so each kind's first page is requested here as well as by the list. With
  //  no search the current kind's total is the list's own first page, which is also what refetches with the list.
  const projectTotal = useScopeKindTotal('projects');
  const releaseGroupTotal = useScopeKindTotal('releaseGroups');
  const hasOwnTotal = search === '';
  const totalCount = hasOwnTotal ? matchCount : (isReleaseGroups ? releaseGroupTotal : projectTotal);
  const kindChoices = kindOptions(
    !isReleaseGroups && hasOwnTotal ? matchCount : projectTotal,
    isReleaseGroups && hasOwnTotal ? matchCount : releaseGroupTotal,
  );
  //  The shown results may be out of date: a newer search is pending, or the current one is refetching.
  const isRefreshing = hasKindPages
    && (search !== debouncedSearch || (results.isFetching && !results.isFetchingNextPage));

  const onToggle = (option: ScopeOption, isChecked: boolean) => {
    const ids = isChecked ? [...selected, option.value] : selected.filter((id) => id !== option.value);
    if (isReleaseGroups) {
      //  The list already gave us the group's title; seed it so its Selected row does not refetch the group.
      if (isChecked) {
        queryClient.setQueryData(getReleaseGroupTitleQueryKey(option.value), option.label);
      }
      setQuery(serializeLegalAgentQuery({ ...query, releaseGroups: ids }));
      return;
    }
    if (isChecked) {
      setPickedOptions(new Map(pickedOptions).set(option.value, option));
    }
    setQuery(serializeLegalAgentQuery({ ...query, projects: ids }));
  };

  const onClear = () => {
    setQuery(serializeLegalAgentQuery({ ...query, [isReleaseGroups ? 'releaseGroups' : 'projects']: [] }));
  };

  const [isSelectingAll, setIsSelectingAll] = useState(false);

  //  The bulk request outlives the render that started it, so its result is applied to the query as it is by then.
  //  The typed search, not the debounced one, so text still waiting on the debounce also drops the result.
  const latest = useRef({ query, search });
  useEffect(() => {
    latest.current = { query, search };
  });

  const onSelectAllMatching = async () => {
    if (matchCount === null || isSelectingAll) {
      return;
    }
    const requested = { scope: query.scope, search: debouncedSearch };
    setIsSelectingAll(true);
    try {
      const options = await loadAllMatchingOptions(requested.scope, requested.search, matchCount);
      const current = latest.current;
      if (current.query.scope !== requested.scope || current.search !== requested.search) {
        return;
      }
      const isGroups = requested.scope === 'releaseGroups';
      const selectedNow = isGroups ? current.query.releaseGroups : current.query.projects;
      const added = options.filter((option) => !selectedNow.includes(option.value));
      const ids = [...selectedNow, ...added.map((option) => option.value)];
      if (isGroups) {
        //  Seed every title so the Selected rows do not fetch each group on their own.
        added.forEach((option) => queryClient.setQueryData(getReleaseGroupTitleQueryKey(option.value), option.label));
        setQuery(serializeLegalAgentQuery({ ...current.query, releaseGroups: ids }));
        return;
      }
      setPickedOptions((picked) => new Map([...picked, ...added.map((option): [string, ScopeOption] => [option.value, option])]));
      setQuery(serializeLegalAgentQuery({ ...current.query, projects: ids }));
    } catch {
      addToast({
        type: 'error',
        content: `${isReleaseGroups ? 'Release groups' : 'Projects'} couldn't be loaded. Try again in a moment.`,
      });
    } finally {
      setIsSelectingAll(false);
    }
  };

  return (
    <div className={styles.scopeSelect} data-test-id="legal-agent-scope-select">
      <LabeledSelect<KindOption>
        id="legal-agent-scope-kind"
        label="Work with"
        options={kindChoices}
        value={kindChoices.find((option) => option.value === query.scope) ?? kindChoices[0]}
        isSearchable={false}
        onChange={(option) => {
          if (option && option.value !== query.scope) {
            setSearch('');
            setQuery(serializeLegalAgentQuery(withScopeKind(query, option.value)));
          }
        }}
      />
      <LegalAgentScopeList
        noun={isReleaseGroups ? 'release group' : 'project'}
        search={search}
        onSearchChange={(value) => setSearch(value)}
        selected={selectedOptions}
        results={_.uniqBy(kindPages.flatMap((page) => page.options), 'value')}
        matchCount={matchCount}
        totalCount={totalCount}
        query={{
          hasNextPage: hasKindPages && results.hasNextPage,
          isFetching: results.isFetching,
          isError: results.isError,
          fetchNextPage: results.fetchNextPage,
          refetch: results.refetch,
        }}
        isRefreshing={isRefreshing}
        onToggle={onToggle}
        onClear={onClear}
        onSelectAllMatching={() => { void onSelectAllMatching(); }}
        isSelectingAll={isSelectingAll}
      />
    </div>
  );
};
